(function () {
  'use strict';

  /*
    Reveals the care guide copy that matches the visitor's hardiness zone.

    Every zone variant is already in the DOM, hidden, so this never fetches
    content. The only network call is the ZIP lookup table, and it is deferred
    until someone actually submits a ZIP -- it is half a megabyte, which is
    worth paying for on demand but not on every product page view.
  */

  var ZONE_STORAGE_KEY = 'buchholz_usda_zone';
  var ZIP_STORAGE_KEY = 'buchholz_zip';
  var ZONE_PATTERN = /^(1[0-3]|[1-9])([ab])$/;

  var zipLookupPromise = null;
  var mapPromise = null;
  var geocodeCache = {};

  /*
    Public ZIP geocoder, no key required. Only the pin on the map depends on
    it; the zone itself is resolved from a table shipped with the theme.
  */
  var GEOCODE_ENDPOINT = 'https://api.zippopotam.us/us/';
  var GEOCODE_STORAGE_PREFIX = 'buchholz_zip_at_';

  /* How much of the map to show around a located ZIP, in the SVG's units. */
  var LOCATED_SPAN = 430;

  /* Matches the aspect the stage reserves, so the map fills it without letterboxing. */
  var MAP_ASPECT = 10 / 16;

  /*
    The artwork's you-are-here icon is drawn for a full-screen map and covers
    most of a state at the sizes used here. Scaling the whole icon thins its
    ring along with its radius, which keeps the proportions it was drawn with.
  */
  var MARKER_SCALE = 0.62;

  /*
    Half-zones are compared on a 1..26 scale so 7a and 7b can be ordered
    against each other. A product range like "4-8" covers all of zone 4
    through all of zone 8, hence 4a's index at the bottom and 8b's at the top.
  */
  function zoneToIndex(zone) {
    var match = ZONE_PATTERN.exec(String(zone || '').trim().toLowerCase());
    if (!match) return null;
    return parseInt(match[1], 10) * 2 - (match[2] === 'a' ? 1 : 0);
  }

  function zoneToNumber(zone) {
    var match = ZONE_PATTERN.exec(String(zone || '').trim().toLowerCase());
    return match ? parseInt(match[1], 10) : null;
  }

  function normalizeZip(value) {
    var digits = String(value || '').replace(/\D/g, '');
    return digits.length >= 5 ? digits.slice(0, 5) : '';
  }

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Private browsing. The zone still applies for this page view. */
    }
  }

  function parseProductRange(root) {
    var raw = root.getAttribute('data-care-guide-zone-range');
    if (!raw) return null;

    try {
      var parsed = JSON.parse(raw);
      if (typeof parsed.min !== 'number' || typeof parsed.max !== 'number') return null;

      return {
        min: parsed.min,
        max: parsed.max,
        display: parsed.display || parsed.min + '-' + parsed.max,
        minIndex: parsed.min * 2 - 1,
        maxIndex: parsed.max * 2
      };
    } catch (error) {
      return null;
    }
  }

  /*
    Groups are relative to the plant: zone 8 is the comfortable middle of a
    7-10 plant and the hot limit of a 4-8 one, so the same visitor must land in
    different groups on different products. Out-of-range is decided first
    because it is the answer that changes a purchase.

    `warm_edge` only appears once a range is wide enough to have a shoulder
    distinct from its top zone; below that the middle stays `core` rather than
    leaving a three-zone plant with no comfortable band at all.
  */
  function resolveZoneGroup(zone, productRange) {
    var index = zoneToIndex(zone);
    var number = zoneToNumber(zone);
    if (index === null) return 'default';

    /*
      Every group below "default" is worded relative to the plant's own range
      -- "cold-edge care" means the edge of *this* plant's tolerance. With no
      published range there is nothing to be relative to, so the general
      guidance is the only honest answer.
    */
    if (!productRange) return 'default';

    if (index < productRange.minIndex) return 'colder';
    if (index > productRange.maxIndex) return 'warmer';

    var span = productRange.max - productRange.min + 1;
    if (span === 1) return 'core';

    if (number === productRange.min) return 'cold_edge';
    if (number === productRange.max) return 'heat_edge';
    if (span >= 4 && number === productRange.max - 1) return 'warm_edge';

    return 'core';
  }

  function loadZipLookup(url) {
    if (zipLookupPromise) return zipLookupPromise;

    zipLookupPromise = fetch(url, { cache: 'force-cache' })
      .then(function (response) {
        if (!response.ok) throw new Error('ZIP lookup unavailable');
        return response.json();
      })
      .catch(function (error) {
        zipLookupPromise = null;
        throw error;
      });

    return zipLookupPromise;
  }

  /*
    The map is drawn in EPSG:5070, the Albers equal-area conic the USDA uses
    for the contiguous states. Its parameters are fixed by that definition;
    the placement of the result inside the artwork is not, so the four
    data-proj-* attributes come off the SVG rather than being repeated here.

    Verified against the map's own 48 state labels: the worst of them sits
    7px from where this puts its state, on a 1200px-wide drawing.
  */
  var ALBERS = { parallel1: 29.5, parallel2: 45.5, latitude0: 23, longitude0: -96 };
  var DEGREES = Math.PI / 180;

  function projectLatLon(latitude, longitude) {
    var sin1 = Math.sin(ALBERS.parallel1 * DEGREES);
    var n = (sin1 + Math.sin(ALBERS.parallel2 * DEGREES)) / 2;
    var c = Math.pow(Math.cos(ALBERS.parallel1 * DEGREES), 2) + 2 * n * sin1;

    var rho0 = Math.sqrt(c - 2 * n * Math.sin(ALBERS.latitude0 * DEGREES)) / n;
    var rho = Math.sqrt(c - 2 * n * Math.sin(latitude * DEGREES)) / n;
    var theta = n * (longitude - ALBERS.longitude0) * DEGREES;

    return { x: rho * Math.sin(theta), y: rho0 - rho * Math.cos(theta) };
  }

  function readProjection(svg) {
    var scale = parseFloat(svg.getAttribute('data-proj-scale'));
    var minX = parseFloat(svg.getAttribute('data-proj-min-x'));
    var maxY = parseFloat(svg.getAttribute('data-proj-max-y'));
    var pad = parseFloat(svg.getAttribute('data-proj-pad'));

    if (!isFinite(scale) || !isFinite(minX) || !isFinite(maxY) || !isFinite(pad)) return null;
    return { scale: scale, minX: minX, maxY: maxY, pad: pad };
  }

  /* Geographic coordinates to a point in the SVG's own units. */
  function locateOnMap(svg, latitude, longitude) {
    var projection = readProjection(svg);
    if (!projection) return null;

    var point = projectLatLon(latitude, longitude);

    return {
      x: projection.pad + (point.x - projection.minX) * projection.scale,
      y: projection.pad + (projection.maxY - point.y) * projection.scale,
    };
  }

  /*
    Repaints the artwork by what each band means for this plant: the zones it
    is listed for, the visitor's own zone, and everything else. The USDA's own
    colours answer "which zone is this", which nobody is asking on a product
    page; the question here is "will it grow where I live".

    The listed range needs no ZIP, so the map answers that much the moment it
    loads. Without a range there is nothing to judge against, and the artwork
    keeps its original colours rather than implying a verdict.
  */
  function shadeMap(svg, productRange, zone) {
    if (!svg || !productRange) return;

    var zoneIndex = zoneToIndex(zone);

    Array.prototype.forEach.call(
      svg.querySelectorAll('.usda-zone-map__zone'),
      function (band) {
        var index = zoneToIndex(band.getAttribute('data-zone'));
        var inRange = index !== null
          && index >= productRange.minIndex
          && index <= productRange.maxIndex;

        band.classList.toggle('is-in-range', inRange);
        band.classList.toggle('is-out-of-range', index !== null && !inRange);
        band.classList.toggle('is-your-zone', index !== null && index === zoneIndex);
      }
    );

    svg.classList.add('is-shaded');
  }

  /* The band the visitor is standing in only exists once they tell us a ZIP. */
  function shadeStage(root, productRange, zone) {
    shadeMap(root.querySelector('[data-care-guide-zone-map-stage] svg'), productRange, zone);

    var legendYou = root.querySelector('[data-care-guide-zone-legend-you]');
    if (legendYou) legendYou.hidden = zoneToIndex(zone) === null;
  }

  function applyZone(root, zone, productRange) {
    var group = resolveZoneGroup(zone, productRange);
    var normalizedZone = String(zone || '').trim().toLowerCase();

    root.setAttribute('data-care-guide-zone-group', group);
    shadeStage(root, productRange, normalizedZone);

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-care-guide-zone-copy]'),
      function (note) {
        note.hidden = note.getAttribute('data-care-guide-zone-copy') !== group;
      }
    );

    var value = root.querySelector('[data-care-guide-zone-value]');
    if (value && ZONE_PATTERN.test(normalizedZone)) {
      value.textContent = 'USDA Zone ' + normalizedZone;
    }

    var fit = root.querySelector('[data-care-guide-zone-fit]');
    if (fit && productRange) {
      fit.textContent = 'This plant is listed for Zones ' + productRange.display + '.';
      fit.hidden = false;
    }
  }

  function setupZipForm(root, productRange) {
    var form = root.querySelector('[data-care-guide-zip-form]');
    var input = root.querySelector('[data-care-guide-zip-input]');
    var status = root.querySelector('[data-care-guide-zip-status]');
    var lookupUrl = root.getAttribute('data-care-guide-zip-data');

    if (!form || !input || !lookupUrl) return;

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      var zip = normalizeZip(input.value);
      if (!zip) {
        if (status) status.textContent = 'Enter a 5-digit ZIP code.';
        return;
      }

      if (status) status.textContent = 'Checking...';

      loadZipLookup(lookupUrl)
        .then(function (lookup) {
          var zone = lookup[zip];

          if (!zone) {
            if (status) status.textContent = 'We could not find that ZIP code.';
            return;
          }

          writeStorage(ZONE_STORAGE_KEY, zone);
          writeStorage(ZIP_STORAGE_KEY, zip);
          if (status) status.textContent = '';

          applyZone(root, zone, productRange);
          locateZip(root, zip);
        })
        .catch(function () {
          if (status) status.textContent = 'Zone lookup is unavailable right now.';
        });
    });
  }

  /*
    The map is a large vector file and the guide sits near the foot of a
    product page, so it is fetched when the guide comes into view rather than
    on load. Everything else on the card works without it.
  */
  function loadMap(root) {
    if (mapPromise) return mapPromise;

    var source = root.getAttribute('data-care-guide-map-src');
    if (!source) return Promise.reject(new Error('no map source'));

    mapPromise = fetch(source, { cache: 'force-cache' })
      .then(function (response) {
        if (!response.ok) throw new Error('map unavailable');
        return response.text();
      })
      .then(function (markup) {
        var parser = new DOMParser();

        /*
          SVG is XML, so a single valueless attribute anywhere in the artwork
          fails the whole parse. The HTML parser tolerates that, so it is worth
          a second attempt before giving up on the map entirely.
        */
        var parsed = parser.parseFromString(markup, 'image/svg+xml');
        var svg = parsed.querySelector('parsererror') ? null : parsed.querySelector('svg');

        if (!svg) svg = parser.parseFromString(markup, 'text/html').querySelector('svg');
        if (!svg) throw new Error('map could not be read');

        return svg;
      })
      .catch(function (error) {
        mapPromise = null;
        throw error;
      });

    return mapPromise;
  }

  function whenVisible(element, callback) {
    if (typeof IntersectionObserver !== 'function') {
      callback();
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;

      observer.disconnect();
      callback();
    }, { rootMargin: '400px' });

    observer.observe(element);
  }

  /*
    A window of the map centred on a point, in the SVG's own units, so the
    view covers the same real-world area wherever the visitor is.

    Clamped to the artwork: without it a ZIP near an edge, which is to say
    anywhere coastal, frames empty space beside the country.
  */
  function framedViewBox(point, span, bounds) {
    var height = span * MAP_ASPECT;
    var x = point.x - span / 2;
    var y = point.y - height / 2;

    x = Math.max(0, Math.min(x, bounds.width - span));
    y = Math.max(0, Math.min(y, bounds.height - height));

    return x + ' ' + y + ' ' + span + ' ' + height;
  }

  function frameMap(svg, point, span) {
    if (!point) {
      svg.setAttribute('viewBox', baseViewBox(svg));
      return;
    }

    var base = baseViewBox(svg).split(/\s+/);
    var bounds = { width: parseFloat(base[2]), height: parseFloat(base[3]) };

    svg.setAttribute('viewBox', framedViewBox(point, span, bounds));
  }

  function baseViewBox(svg) {
    return svg.getAttribute('data-care-guide-base-viewbox') || '0 0 1200 760';
  }

  /*
    The marker is a copy of the icon the artwork keeps in <defs>, not a <use>
    referencing it. A <use> renders its content in a shadow tree that the
    page's selectors cannot reach, so the circles would inherit the root's
    fill="none" and paint nothing. Copying puts real nodes in the document
    where the stylesheet can colour them.
  */
  function markMap(svg, point) {
    var existing = svg.querySelector('[data-care-guide-marker]');
    if (existing) existing.parentNode.removeChild(existing);
    if (!point) return;

    var icon = svg.querySelector('#usda-you-are-here-icon');
    if (!icon) return;

    var marker = icon.cloneNode(true);
    marker.removeAttribute('id');
    marker.setAttribute('data-care-guide-marker', '');
    marker.setAttribute(
      'transform',
      'translate(' + point.x + ' ' + point.y + ') scale(' + MARKER_SCALE + ')'
    );

    svg.appendChild(marker);
  }

  /*
    Coordinates for a ZIP. The zone itself comes from the bundled table, so a
    failure here costs the pin on the map and nothing else.
  */
  function geocodeZip(zip) {
    if (geocodeCache[zip]) return Promise.resolve(geocodeCache[zip]);

    return fetch(GEOCODE_ENDPOINT + encodeURIComponent(zip), { cache: 'force-cache' })
      .then(function (response) {
        if (!response.ok) throw new Error('ZIP location unavailable');
        return response.json();
      })
      .then(function (data) {
        var place = data && data.places && data.places[0];
        if (!place) throw new Error('ZIP has no location');

        var latitude = parseFloat(place.latitude);
        var longitude = parseFloat(place.longitude);
        if (!isFinite(latitude) || !isFinite(longitude)) throw new Error('ZIP location unreadable');

        var located = {
          latitude: latitude,
          longitude: longitude,
          place: [place['place name'], place['state abbreviation']].filter(Boolean).join(', '),
        };

        geocodeCache[zip] = located;
        writeStorage(GEOCODE_STORAGE_PREFIX + zip, JSON.stringify(located));
        return located;
      });
  }

  function readCachedGeocode(zip) {
    if (geocodeCache[zip]) return geocodeCache[zip];

    var stored = readStorage(GEOCODE_STORAGE_PREFIX + zip);
    if (!stored) return null;

    try {
      geocodeCache[zip] = JSON.parse(stored);
      return geocodeCache[zip];
    } catch (error) {
      return null;
    }
  }

  /* Paints the map on a located ZIP, or leaves it showing the whole country. */
  function paintLocation(root, zip, located) {
    var svg = root.querySelector('[data-care-guide-zone-map-stage] svg');
    var caption = root.querySelector('[data-care-guide-zone-map-caption]');
    if (!svg) return;

    var point = located ? locateOnMap(svg, located.latitude, located.longitude) : null;

    frameMap(svg, point, LOCATED_SPAN);
    markMap(svg, point);

    if (caption && located && located.place) {
      caption.textContent = 'ZIP ' + zip + ' \u00b7 ' + located.place;
    }
  }

  function locateZip(root, zip) {
    var cached = readCachedGeocode(zip);
    if (cached) {
      paintLocation(root, zip, cached);
      return;
    }

    geocodeZip(zip)
      .then(function (located) {
        paintLocation(root, zip, located);
      })
      .catch(function () {
        /* The zone is already shown; the map simply stays on the whole country. */
      });
  }

  function setupZoneMap(root, productRange) {
    var stage = root.querySelector('[data-care-guide-zone-map-stage]');
    var loading = root.querySelector('[data-care-guide-zone-map-loading]');
    if (!stage) return;

    whenVisible(root, function () {
      loadMap(root)
        .then(function (svg) {
          var map = svg.cloneNode(true);
          map.setAttribute('data-care-guide-base-viewbox', map.getAttribute('viewBox') || '0 0 1200 760');
          map.removeAttribute('width');
          map.removeAttribute('height');

          if (loading && loading.parentNode) loading.parentNode.removeChild(loading);
          stage.appendChild(map);

          /* The zone may have been restored before the artwork arrived. */
          shadeStage(root, productRange, readStorage(ZONE_STORAGE_KEY));

          var zip = normalizeZip(readStorage(ZIP_STORAGE_KEY));
          if (zip) locateZip(root, zip);
        })
        .catch(function () {
          if (loading) loading.textContent = 'The hardiness map could not be loaded.';
        });
    });
  }

  /* The whole country, in an overlay, for anyone who wants the wider picture. */
  function setupMapOverlay(root, productRange) {
    var overlay = root.querySelector('[data-care-guide-zone-map-overlay]');
    var opener = root.querySelector('[data-care-guide-zone-map-open]');
    var holder = root.querySelector('[data-care-guide-zone-map-full]');
    if (!overlay || !opener || !holder) return;

    function close() {
      overlay.hidden = true;
      opener.focus();
    }

    opener.addEventListener('click', function () {
      loadMap(root).then(function (svg) {
        holder.innerHTML = '';

        var map = svg.cloneNode(true);
        map.removeAttribute('width');
        map.removeAttribute('height');
        holder.appendChild(map);

        shadeMap(map, productRange, readStorage(ZONE_STORAGE_KEY));

        var zip = normalizeZip(readStorage(ZIP_STORAGE_KEY));
        var located = zip ? readCachedGeocode(zip) : null;
        if (located) markMap(map, locateOnMap(map, located.latitude, located.longitude));
      });

      overlay.hidden = false;

      var closer = overlay.querySelector('.care-guide__zone-map-close');
      if (closer) closer.focus();
    });

    Array.prototype.forEach.call(
      overlay.querySelectorAll('[data-care-guide-zone-map-close]'),
      function (element) {
        element.addEventListener('click', close);
      }
    );

    /*
      Bound to the document rather than the guide: once the overlay is open it
      covers the page, and a keypress may not be inside the guide's subtree.
    */
    document.addEventListener('keydown', function (event) {
      if (overlay.hidden) return;
      if (event.key !== 'Escape' && event.key !== 'Esc') return;

      close();
    });
  }

  /*
    A saved zone wins over the account address: it is the more deliberate
    signal, and a shopper buying for a second property has no other way to say
    so.
  */
  function restoreZone(root, productRange) {
    var savedZone = readStorage(ZONE_STORAGE_KEY);
    if (ZONE_PATTERN.test(savedZone)) {
      applyZone(root, savedZone, productRange);
      return;
    }

    var customerZip = normalizeZip(root.getAttribute('data-care-guide-customer-zip'));
    var lookupUrl = root.getAttribute('data-care-guide-zip-data');
    if (!customerZip || !lookupUrl) return;

    /*
      Resolving an account address costs the same half-megabyte as a manual
      lookup, and nobody asked for it on this page view. Wait for idle so it
      never competes with the product images, and cache the result so a
      returning customer takes the saved-zone path above instead.
    */
    var resolveFromAccount = function () {
      loadZipLookup(lookupUrl)
        .then(function (lookup) {
          var zone = lookup[customerZip];
          if (!zone) return;

          writeStorage(ZONE_STORAGE_KEY, zone);
          writeStorage(ZIP_STORAGE_KEY, customerZip);

          applyZone(root, zone, productRange);
          locateZip(root, customerZip);
        })
        .catch(function () {
          /* Guide stays on its default copy. */
        });
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(resolveFromAccount, { timeout: 5000 });
    } else {
      window.setTimeout(resolveFromAccount, 2000);
    }
  }

  /*
    Browsers that understand `name` on <details> run the tabs as one exclusive
    accordion themselves, before any script loads. Older ones ignore the
    attribute and let every tab stand open, so this reproduces the behaviour
    for them only.
  */
  function setupExclusiveTabs(root) {
    if ('name' in HTMLDetailsElement.prototype) return;

    var tabs = Array.prototype.slice.call(root.querySelectorAll('details[name]'));

    tabs.forEach(function (tab) {
      tab.addEventListener('toggle', function () {
        if (!tab.open) return;

        tabs.forEach(function (other) {
          /* Closing fires `toggle` again, but that pass returns above. */
          if (other !== tab && other.getAttribute('name') === tab.getAttribute('name')) {
            other.open = false;
          }
        });
      });
    });
  }

  /*
    Opening a tab closes whichever one was open, and when that one stood above
    it the page collapses upward under the reader. The tab they just opened
    ends up off the top of the screen, showing the end of its text or nothing
    at all. Bringing it back to the top puts the content where they are
    already looking.

    The browser handles the easing, and skips it for anyone who has asked
    their system for less motion.
  */
  function setupTabScroll(root) {
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    Array.prototype.forEach.call(
      root.querySelectorAll('details[name]'),
      function (tab) {
        tab.addEventListener('toggle', function () {
          if (!tab.open) return;

          /* After the tab that is closing has given up its height. */
          requestAnimationFrame(function () {
            tab.scrollIntoView({
              behavior: reduced.matches ? 'auto' : 'smooth',
              block: 'start',
            });
          });
        });
      }
    );
  }

  function init() {
    var root = document.querySelector('[data-care-guide]');
    if (!root) return;

    var productRange = parseProductRange(root);

    setupExclusiveTabs(root);
    setupTabScroll(root);
    setupZipForm(root, productRange);
    setupZoneMap(root, productRange);
    setupMapOverlay(root, productRange);
    restoreZone(root, productRange);
  }

  /*
    Exposed for the Node test runner only. `module` is undefined in the
    browser, so this is inert on the storefront.
  */
  if (typeof module === 'object' && module !== null && module.exports) {
    module.exports = {
      zoneToIndex: zoneToIndex,
      zoneToNumber: zoneToNumber,
      normalizeZip: normalizeZip,
      resolveZoneGroup: resolveZoneGroup,
      projectLatLon: projectLatLon,
      framedViewBox: framedViewBox,
      LOCATED_SPAN: LOCATED_SPAN,
    };
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
